import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * ### 空室／空床情報の設定 API 操作クラス
 */
export default class RoomScheduleSettings {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET RoomsScheduleSettings
   */
  public async get(userStore: LoginUser): Promise<RoomScheduleSettingGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.RoomsScheduleSettings, formatParams);
    const response = await this.commonService.executeGet<RoomScheduleSettingGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH RoomsScheduleSettings
   */
  public async patch(userStore: LoginUser, requestBody: RoomScheduleSettingPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.RoomsScheduleSettings, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface RoomScheduleSettingGetModel {
  /** 施設を特定するためのID */
  facilityId: number;
  /** 公開状態 */
  isOpened: boolean;
  /** 公開情報 */
  publicType: PublicType;
  /** 公開開始日（何日後からか） */
  availableDate: number | null;
  /** 公開終了日 */
  endDate: number | null;
  /** 公開情報（自分で設定） */
  customPublicSettings: Array<CustomPublicSetting>;
  /** 連絡事項 */
  message: string;
  /** 仮申し込みを使用するか否か */
  useProvisionalApplication: boolean;
  /** 削除フラグ */
  deleteFlag: boolean;
  /** レコード作成日 */
  created: number;
  /** レコード更新日 */
  updated: number;
}

export enum PublicType {
  /** 空床スケジュールを公開する */
  SCHEDULE = "schedule",
  /** 公開情報を自分で設定する */
  CUSTOM = "custom"
}

export interface CustomPublicSetting {
  /** ID */
  id: number;
  /** 用途 */
  useCase: UseCase;
  /** 部屋種別 */
  roomType: RoomType;
  /** 空床情報 */
  receptionStatus: ReceptionStatus;
  /** 空床情報（空床数） */
  receptionCount: ReceptionCount;
  /** 使用有無 */
  use: boolean;
}

export enum UseCase {
  /** 長期入所 */
  LONG_STAY = 0,
  /** ショートステイ */
  SHORT_STAY = 1,
  /** 長期・短期併用 */
  LONG_AND_SHORT_STAY = 2
}

export enum RoomType {
  /** 個室 */
  SINGLE = 1,
  /** 多床室 */
  MULTI_BED = 2,
  /** 多床室（男性） */
  MULTI_BED_MEN = 3,
  /** 多床室（女性） */
  MULTI_BED_WOMEN = 4,
  /** 男性 */
  MEN = 5,
  /** 女性 */
  WOMEN = 6
}

export enum ReceptionStatus {
  /** × */
  CANT = 0,
  /** ○ */
  CAN = 1,
  /** 電話 */
  TEL = 2
}

export enum ReceptionCount {
  /** 非公開 */
  NOT_PUBLIC = 0,
  /** 1床 */
  BED_1 = 1,
  /** 2床 */
  BED_2 = 2,
  /** 3床 */
  BED_3 = 3,
  /** 4床 */
  BED_4 = 4,
  /** 5床 */
  BED_5 = 5,
  /** 6床 */
  BED_6 = 6,
  /** 7床 */
  BED_7 = 7,
  /** 8床 */
  BED_8 = 8,
  /** 9床 */
  BED_9 = 9,
  /** 10床以上 */
  BED_OVER_10 = 10
}

export interface RoomScheduleSettingPatchModel {
  /** 公開状態 */
  isOpened: boolean;
  /** 公開情報 */
  publicType: PublicType;
  /** 公開開始日（何日後からか） */
  availableDate: number | null;
  /** 公開終了日 */
  endDate: number | null;
  /** 公開情報（自分で設定したもの） */
  customPublicSettings: Array<CustomPublicSetting>;
  /** 連絡事項 */
  message: string;
  /** 仮申し込みを使用するか否か */
  useProvisionalApplication: boolean;
  /** レコード更新日(YYYYMMDDhhmmssSSS) */
  updated: number;
}
